import React from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "@mui/material/SvgIcon";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router";
import moment from "moment";
import { ReactComponent as CalenderMonthIcon } from "assets/images/criticalIncident/calendar.svg";
import { ReactComponent as LocationIcon } from "assets/images/criticalIncident/location_pin.svg";
import { CIPriorityLevel, type CIReferral, CIReferralStatus } from "components/CriticalIncident/interfaces";
import { StatusChip, type StatusChipStatus } from "components/CriticalIncident/components/Shared/StatusChip";
import { Card } from "components/Shared/Card";
import { renderEventMatchingIcon } from "components/Calendaring/utils/eventIconUtils";
import { Button } from "components/Shared/Inputs";
import { isExpiredReferral } from "components/CriticalIncident/utils/CIReferralUtils";
import { TruncatedText } from "components/Shared";
import { formatAddress } from "components/CriticalIncident/utils/formatters";

import './CITodoCard.scss';

export interface CITodoItemProps {
    referral: CIReferral;
    reloadGrid?: () => void;
}


export const CITodoCard: React.FC<CITodoItemProps> = ({ referral, reloadGrid }) => {
    const { t, i18n, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
    const [chips, setChips] = React.useState<React.ReactNode[]>([]);
    const navigate = useNavigate();
    const { city, state = '' } = { ...referral.location };
    const statusRef = React.useRef(referral.status);

    const handleClick = React.useCallback(() => {
        navigate(`/?referralId=${referral?.referralId}`);
    }, [navigate, referral?.referralId]);

    const expiredReferralButtonProps = { label: t('remove'), onClick: () => reloadGrid?.() };
    const acceptedReferralButtonProps = { label: t('view'), onClick: () => handleClick() };
    const pendingReferralButtonProps = { label: t('respond'), onClick: () => handleClick() };

    const TodoButtonPropsMap = {
        [CIReferralStatus.Open]: pendingReferralButtonProps,
        [CIReferralStatus.Accepted]: acceptedReferralButtonProps,
        [CIReferralStatus.Assigned]: acceptedReferralButtonProps,
        [CIReferralStatus.Expired]: expiredReferralButtonProps,
        undefined: pendingReferralButtonProps,
    }

    const [buttonProps, setButtonProps] = React.useState(TodoButtonPropsMap[referral.status]);

    React.useEffect(() => {
        if (isExpiredReferral(referral) || referral.status === CIReferralStatus.Expired) {
            setButtonProps(expiredReferralButtonProps);
            return;
        }
        if (statusRef.current !== referral.status) {
            setButtonProps(TodoButtonPropsMap[referral.status]);
            statusRef.current = referral.status;
        }
    }, [referral.status, referral.validUntil, t, reloadGrid, translationsReady]);

    React.useEffect(() => {
       setButtonProps(TodoButtonPropsMap[referral.status]);
    }, [i18n.language, translationsReady]);

    if (isExpiredReferral(referral)) {
        referral.status = CIReferralStatus.Expired;
    }

    const statusChipProps = React.useMemo((): { label: string; status: StatusChipStatus } => {
        if (isExpiredReferral(referral)) {
            return { label: t('expired'), status: 'error' };
        }
        return ({
            [CIReferralStatus.Open]: { label: t('new'), status: 'default' },
            [CIReferralStatus.Accepted]: { label: t('pending'), status: 'warning' },
            [CIReferralStatus.Declined]: { label: t('declined'), status: 'error' },
            [CIReferralStatus.Assigned]: { label: t('assigned'), status: 'success' },
            [CIReferralStatus.Expired]: { label: t('expired'), status: 'error' },
        } as Record<string, { label: string; status: StatusChipStatus }>)[referral.status ?? CIReferralStatus.Open];
    }, [referral.status, referral.validUntil, t]);

    const priorityChipProps = React.useMemo(() => ({
        [CIPriorityLevel.high]: { label: t('high'), status: 'warning' as StatusChipStatus, showIcon: true },
        [CIPriorityLevel.critical]: { label: t('critical'), status: 'error' as StatusChipStatus, showIcon: true },
    })[referral.priority], [referral.priority, t]);

    React.useEffect(() => {
        const allChips = [
            <StatusChip key="status" {...statusChipProps} />,
            !!priorityChipProps && <StatusChip key="priority" {...priorityChipProps} />,
        ].filter(Boolean);
        setChips(allChips);
    }, [statusChipProps, priorityChipProps]);

    return !!referral && translationsReady ? (
        <Card elevation={1} className="ci_todo_card">
            <div className="ci_todo_card_content">
                <div className="card_header">
                    <h3 className="card_title"><TruncatedText text={referral.companyName} /></h3>
                </div>
                {chips.length > 0 && (
                    <div className="referral_detail">
                        <div className="chips">{chips}</div>
                    </div>
                )}
                <div className="referral_detail">
                    {renderEventMatchingIcon(referral.modality)}<span>{t(referral.modality)}</span>
                </div>
                {['inperson', 'in person'].includes(referral.modality.toLowerCase()) && (!!city?.trim() || !!state?.trim()) && (
                    <div className="referral_detail">
                        <SvgIcon component={LocationIcon} inheritViewBox style={{ color: 'var(--text_primary_color)' }} />
                        <div className="location_details">
                            {/* We want to truncate the city name if it's too long but show "city, state" in the tooltip, hence the tooltip override */}
                            {<TruncatedText tooltipOverride={formatAddress(referral.location, { t, cityAndStateOnly: true })} maxWidth="16.5625rem">{formatAddress(referral.location, { t, cityAndStateOnly: true })}</TruncatedText>}
                        </div>
                    </div>
                )}
                <p className="referral_detail">
                    <SvgIcon component={CalenderMonthIcon} inheritViewBox />
                    <span className="appt_date">{moment(referral.bookingStartTime).calendar(null, { sameElse: t('sameElseStartTime') })}</span>
                </p>
            </div>
            <div className="ci_todo_card_actions_container">
                <Button className="flat_button" variant="text" onClick={buttonProps.onClick}>{buttonProps.label}</Button>
            </div>
        </Card>
    ) : <CITodoCardSkeleton />;
};

export const CITodoCardSkeleton: React.FC = () => {
    return (
        <Card elevation={1} data-testid="ci-todo-card-skeleton" className="ci_todo_card skeleton">
            <div className="ci_todo_card_content">
                <div className="card_header">
                    <Skeleton variant="text" width="90%" />
                </div>
                <div className="referral_detail">
                    <Skeleton variant="text" width="90%" />
                </div>
                <div className="referral_detail">
                    <Skeleton variant="text" width="90%" />
                </div>
                <div className="referral_detail">
                    <Skeleton variant="text" width="90%" />
                </div>
                <div className="referral_detail">
                    <Skeleton variant="text" width="50%" />
                </div>
            </div>
            <div className="ci_todo_card_actions_container">
                <div className="flat_button" style={{ height: '40px' }}></div>
            </div>
        </Card>
    );
}
